import { api, type SharedFolderDetail, type SharedFolderSummary } from '../api/client';
import { applyI18n, t } from '../i18n';
import { bindLocaleRefresh } from '../i18n/bindLocaleRefresh';
import { dialogHelpTopic } from '../help/dialogHelpTopics';
import { bindModalDismiss, createModalShell } from './modalDialogShell';

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString();
}

export function canExtendSharedFolder(folder: SharedFolderSummary): boolean {
  return folder.canExtend === true && folder.maxExtendDays > 0;
}

export function showSharedFolderExtendDialog(
  folder: SharedFolderSummary,
): Promise<SharedFolderDetail | null> {
  return new Promise((resolve) => {
    const maxExtendDays = Math.max(1, folder.maxExtendDays);
    const defaultDays = Math.min(7, maxExtendDays);

    const shell = createModalShell({
      titleKey: 'files.owner.extendTitle',
      titleId: 'files-extend-dialog-title',
      helpTopic: dialogHelpTopic('sharedFolderExtend'),
    });

    shell.body.innerHTML = `
      <p class="muted" id="files-extend-dialog-current"></p>
      <form class="form-grid" id="files-extend-dialog-form">
        <label class="expiry-option">
          <span data-i18n="files.owner.extendBy"></span>
          <input type="number" name="extendDays" min="1" max="${maxExtendDays}" value="${defaultDays}" required />
          <span data-i18n="files.owner.expiresDays"></span>
        </label>
        <p class="error-text" id="files-extend-dialog-error" hidden></p>
      </form>
    `;

    const cancelButton = document.createElement('button');
    cancelButton.type = 'button';
    cancelButton.className = 'button ghost';
    cancelButton.id = 'files-extend-dialog-cancel';
    cancelButton.innerHTML = '<span data-i18n="common.cancel"></span>';

    const submitButton = document.createElement('button');
    submitButton.type = 'submit';
    submitButton.className = 'button primary';
    submitButton.id = 'files-extend-dialog-submit';
    submitButton.setAttribute('form', 'files-extend-dialog-form');
    submitButton.innerHTML = '<span data-i18n="files.owner.extend"></span>';

    shell.actions.append(cancelButton, submitButton);
    document.body.append(shell.backdrop);

    const currentEl = shell.body.querySelector('#files-extend-dialog-current') as HTMLParagraphElement;
    const form = shell.body.querySelector('#files-extend-dialog-form') as HTMLFormElement;
    const errorEl = shell.body.querySelector('#files-extend-dialog-error') as HTMLParagraphElement;
    const extendInput = form.querySelector('input[name="extendDays"]') as HTMLInputElement;
    let submitting = false;

    const refreshLocale = () => {
      currentEl.textContent = `${t('files.owner.extendCurrent')}: ${formatDateTime(folder.expiresAt)}`;
      if (!errorEl.hidden) {
        errorEl.textContent = t('files.owner.extendFailed');
      }
      applyI18n(shell.dialog);
    };

    refreshLocale();
    const unbindLocale = bindLocaleRefresh(refreshLocale);

    const close = (updated: SharedFolderDetail | null) => {
      if (submitting) {
        return;
      }

      unbindLocale();
      unbindDismiss();
      shell.backdrop.remove();
      resolve(updated);
    };

    const unbindDismiss = bindModalDismiss({
      backdrop: shell.backdrop,
      closeButton: shell.closeButton,
      onClose: () => close(null),
    });

    cancelButton.addEventListener('click', () => close(null));

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (submitting) {
        return;
      }

      void (async () => {
        submitting = true;
        errorEl.hidden = true;
        submitButton.disabled = true;

        try {
          const extendDays = Number.parseInt(String(new FormData(form).get('extendDays') ?? ''), 10);
          if (!Number.isFinite(extendDays) || extendDays < 1 || extendDays > maxExtendDays) {
            throw new Error('invalid_extend_days');
          }

          const currentExpiry = new Date(folder.expiresAt);
          const newExpiresAt = new Date(
            currentExpiry.getTime() + extendDays * 24 * 60 * 60 * 1000,
          ).toISOString();

          const updated = await api.patchSharedFolder(folder.id, { expiresAt: newExpiresAt });
          submitting = false;
          unbindLocale();
          unbindDismiss();
          shell.backdrop.remove();
          resolve(updated);
        } catch {
          submitting = false;
          submitButton.disabled = false;
          errorEl.hidden = false;
          errorEl.textContent = t('files.owner.extendFailed');
        }
      })();
    });

    extendInput.focus();
    extendInput.select();
  });
}
